import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { FilterPill } from '../../components/ui';
import { useTicketActions } from '../../hooks/useTicketActions';
import { useTicketList } from '../../hooks/useTicketList';
import { getTicket, listTicketRuns } from '../../services/api/tickets';
import { getApiErrorDetails } from '../../utils/api-error';
import { cn } from '../../utils/cn';

import { initialTicketFilter, isTicketOutsideList } from './sessions-tab';
import { TicketActions } from './TicketActions';
import { TicketDetail } from './TicketDetail';
import { TICKET_STATUS_COLORS, TICKET_STATUS_FILTERS } from './ticket-labels';
import { TicketRunBanner } from './TicketRunBanner';
import { TicketTable } from './TicketTable';

interface TicketsPanelProps {
  focusTicketId?: string | null;
  onOpenSession?: (sessionId: string) => void;
}

export const TicketsPanel = ({ focusTicketId = null, onOpenSession }: TicketsPanelProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(focusTicketId);
  const list = useTicketList(initialTicketFilter(focusTicketId));
  const actions = useTicketActions({ onSettled: list.reset });

  const detailQuery = useQuery({
    queryKey: ['ticket', expandedId],
    queryFn: () => getTicket(expandedId!),
    enabled: Boolean(expandedId),
  });

  const runsQuery = useQuery({
    queryKey: ['ticket-runs'],
    queryFn: () => listTicketRuns(1),
    staleTime: 60_000,
  });

  const ticket = detailQuery.data?.ticket ?? null;

  return (
    <div className="space-y-4">
      <TicketRunBanner run={runsQuery.data?.runs?.[0] ?? null} />

      {actions.banner && (
        <div
          className={cn(
            'rounded-lg border px-3 py-2 text-sm',
            actions.banner.isError
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-green-200 bg-green-50 text-green-800',
          )}
        >
          {actions.banner.message}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Status:</span>
        {TICKET_STATUS_FILTERS.map((pill) => (
          <FilterPill
            key={pill.key}
            active={list.statusFilter === pill.key}
            tone={
              pill.key === 'all'
                ? 'bg-slate-100 text-slate-700 border-slate-200'
                : TICKET_STATUS_COLORS[pill.key]
            }
            onClick={() => {
              list.setStatusFilter(pill.key);
              setExpandedId(null);
            }}
          >
            {pill.label}
          </FilterPill>
        ))}
        {list.total !== null && <span className="text-slate-400">({list.total} totalt)</span>}
      </div>

      {!list.isLoading && isTicketOutsideList(expandedId, list.items) && (
        <div className="overflow-hidden rounded-lg border border-indigo-200 bg-white">
          <TicketDetail
            ticket={ticket}
            isLoading={detailQuery.isLoading}
            error={detailQuery.error}
            onOpenSession={onOpenSession}
            actions={
              ticket ? (
                <TicketActions
                  ticketId={ticket.id}
                  isApproving={actions.approve.isPending}
                  isRejecting={actions.reject.isPending}
                  onApprove={(id) => actions.approve.mutate(id)}
                  onReject={(id, reason) => actions.reject.mutate({ id, reason })}
                />
              ) : null
            }
          />
        </div>
      )}

      {list.isLoading && list.items.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
        </div>
      ) : list.error ? (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>Kunde inte ladda tickets: {getApiErrorDetails(list.error)}</span>
        </div>
      ) : list.items.length === 0 ? (
        <div className="p-8 text-center rounded-lg border border-slate-200 bg-slate-50">
          <p className="text-slate-600">
            {list.statusFilter === 'draft'
              ? 'Inga utkast i kön — antingen en lugn natt eller så har allt redan hanterats.'
              : 'Inga tickets med den här statusen.'}
          </p>
        </div>
      ) : (
        <TicketTable
          items={list.items}
          expandedId={expandedId}
          onToggle={(id) => setExpandedId(id === expandedId ? null : id)}
          renderDetail={(item) => (
            <TicketDetail
              ticket={ticket?.id === item.id ? ticket : null}
              isLoading={detailQuery.isLoading}
              error={detailQuery.error}
              onOpenSession={onOpenSession}
              actions={
                <TicketActions
                  ticketId={item.id}
                  isApproving={actions.approve.isPending}
                  isRejecting={actions.reject.isPending}
                  onApprove={(id) => actions.approve.mutate(id)}
                  onReject={(id, reason) => actions.reject.mutate({ id, reason })}
                />
              }
            />
          )}
        />
      )}

      {list.hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            disabled={list.isFetching}
            onClick={list.loadMore}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {list.isFetching ? 'Laddar…' : 'Ladda fler'}
          </button>
        </div>
      )}
    </div>
  );
};
